import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "motion/react";
import LaunchIcon from "@mui/icons-material/Launch";
import useStudentsPageQueries from "../../store/students/studentQueries";
import { getAccountType } from "../../store/auth/selectors";
import { CONFIG_VARS } from "../../constants/urlConstants/configuration";
import {
  checkPermission,
  PERMISSION_TYPES,
} from "../../shared/utils/permissions";
import EventsSection from "../../shared/components/EventsSection/EventsSection";
import UploadButton from "../../shared/components/UploadButton/UploadButton";
import StudentTable from "../../shared/components/Table/StudentTable";
import Pagination from "../../shared/components/Pagination/Pagination";
import AnimatedCount from "../../shared/components/AnimatedCount/AnimatedCount";
import downloading from "../../assets/gifs/downloading.gif";

const isWithinNext7Days = (dateString) => {
  if (!dateString) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const eventDate = new Date(dateString);
  eventDate.setHours(0, 0, 0, 0);

  const sevenDaysFromEvent = new Date(eventDate);
  sevenDaysFromEvent.setDate(eventDate.getDate() + 7);

  return today <= sevenDaysFromEvent;
};

const generateDeeplink = (student, selectedEvent) => {
  if (!student || !selectedEvent) return "";

  const currentEnv = CONFIG_VARS.currentEnvironment;

  // Check if this is a "Scholarship Test" event (case insensitive)
  const isScholarshipTest =
    selectedEvent.event &&
    selectedEvent.event.toLowerCase().includes("scholarship test");

  // Select the appropriate base URL based on event type
  const baseUrl = isScholarshipTest
    ? CONFIG_VARS.scholarshipTestBaseUrl[currentEnv]
    : CONFIG_VARS.deeplinkBaseUrl[currentEnv];

  let examStatus = "missed";
  if (student.attempt === "Yes") {
    examStatus = "completed";
  } else if (
    selectedEvent?.start_date_time &&
    isWithinNext7Days(selectedEvent.start_date_time)
  ) {
    examStatus = "scheduled";
  }
  // Build params array with event-specific info
  const params = [
    `exam_status=${examStatus}`,
    `crnId=${student.crn_id || ""}`,
    `eventId=${student.event_id || ""}`,
    `id=${student.loginId || ""}`,
    `password=qwerty`,
  ];

  return `${baseUrl}deeplink/exampage?${params.join("&")}`;
};

const EventsTabContent = ({
  events,
  onEventClick,
  eventLoader,
  selectedEvent,
  error,
  eventStudentsTotal,
  eventAttemptedTotal,
  eventCurrentPage,
  eventItemsPerPage,
  onEventPageChange,
  onEventItemsPerPageChange,
  paginatedStudents,
  columns,
  selectedStudents,
  onSelectAll,
  onSelectRow,
  schoolId,
  attemptFilter,
  onAttemptFilterChange,
  refetchEventStudents,
  isFetchingEventStudents,
  schoolDetails,
}) => {
  const accountType = useSelector(getAccountType);
  const canUploadToEvent = useSelector(
    (state) => state.uploadPermissions.canUploadToEvent,
  );
  const checkUserPermission = (permissionType) =>
    checkPermission(accountType, permissionType);

  const canUploadStudents =
    checkUserPermission(PERMISSION_TYPES.UPLOAD_STUDENTS) && canUploadToEvent;
  const canExportData = checkUserPermission(
    PERMISSION_TYPES.EXPORT_STUDENT_DATA,
  );

  const { handleExportStudentsByEvent } = useStudentsPageQueries();
  const [isExporting, setIsExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState({ type: null, text: "" });

  const handleExport = async (exportFormat) => {
    if (!selectedEvent?.scheduled_events) return;

    setIsExporting(true);
    setExportMessage({ type: null, text: "" });

    try {
      const response = await handleExportStudentsByEvent(
        selectedEvent.scheduled_events,
        attemptFilter,
        exportFormat,
      );

      if (response?.export_url) {
        const link = document.createElement("a");
        link.href = response.export_url;
        link.setAttribute("target", "_blank");
        link.setAttribute(
          "download",
          `students_export_${
            selectedEvent.event || "data"
          }_${attemptFilter}.${exportFormat}`,
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setExportMessage({
          type: "success",
          text: `${exportFormat.toUpperCase()} export successful`,
        });
      }
    } catch (error) {
      console.error(`Error exporting students data as ${exportFormat}:`, error);
      setExportMessage({
        type: "error",
        text: `Failed to export ${exportFormat.toUpperCase()}`,
      });
    } finally {
      setIsExporting(false);

      setTimeout(() => {
        setExportMessage({ type: null, text: "" });
      }, 3000);
    }
  };

  const handleViewSchoolReport = () => {
    if (!selectedEvent?.scheduled_events || !schoolId) return;

    const eventId = selectedEvent.scheduled_events[0];
    const url = `${CONFIG_VARS.schoolReportBaseUrl}/${schoolId}/${eventId}`;
    window.open(url, "_blank");
  };

  const totalAttempted = eventAttemptedTotal || 0;

  const eventContentKey = useMemo(() => {
    if (!selectedEvent) return "no-event";
    const parts = [
      selectedEvent.event ?? "event",
      selectedEvent.start_date_time ?? "start",
      selectedEvent.end_date_time ?? "end",
      ...(selectedEvent.scheduled_events ?? []),
    ].filter(Boolean);
    return parts.join("__");
  }, [selectedEvent]);

  const [countsReady, setCountsReady] = useState(true);
  const [displayTotals, setDisplayTotals] = useState({
    students: eventStudentsTotal,
    attempted: totalAttempted,
  });
  const isFirstRenderRef = useRef(true);
  const countTimerRef = useRef(null);

  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    setCountsReady(false);
    if (countTimerRef.current) {
      clearTimeout(countTimerRef.current);
    }
    countTimerRef.current = setTimeout(() => {
      setCountsReady(true);
      countTimerRef.current = null;
    }, 175);
  }, [eventContentKey]);

  useEffect(() => {
    if (!countsReady) return;
    setDisplayTotals({
      students: eventStudentsTotal,
      attempted: totalAttempted,
    });
  }, [countsReady, eventStudentsTotal, totalAttempted]);

  useEffect(() => {
    return () => {
      if (countTimerRef.current) {
        clearTimeout(countTimerRef.current);
      }
    };
  }, []);

  const studentsWithDeeplinks = useMemo(() => {
    return (
      paginatedStudents?.map((student) => ({
        ...student,
        deepLink: generateDeeplink(student, selectedEvent),
      })) || []
    );
  }, [paginatedStudents, selectedEvent]);

  return (
    <div className="events-tab-content">
      <div className="events-tab-header">
        <EventsSection
          events={events}
          onEventClick={onEventClick}
          eventLoader={eventLoader}
          activeEvent={selectedEvent}
          refetchEventStudents={refetchEventStudents}
          isFetchingEventStudents={isFetchingEventStudents}
          schoolId={schoolId}
        />
        {schoolId && canUploadStudents && (
          <UploadButton
            schoolId={schoolId}
            selectedEvent={selectedEvent}
            uploadType={"studentRegister"}
            selectedTab="Events"
            onUploadSuccess={refetchEventStudents}
            schoolDetails={schoolDetails}
          />
        )}
      </div>
      {error && <div className="error-message">{error}</div>}
      {selectedEvent ? (
        <>
          <div className="students-info">
            <span style={{ whiteSpace: "nowrap", fontWeight: "500" }}>
              Total Students: <AnimatedCount value={displayTotals.students} /> |
              Total Attempted: <AnimatedCount value={displayTotals.attempted} />
            </span>
            <motion.div
              className="table-controls"
              layout
              transition={{
                layout: { duration: 0.45, ease: [0.25, 0.1, 0.25, 1] },
              }}
            >
              {selectedEvent && schoolId && (
                <button
                  className="view-school-report-button"
                  onClick={handleViewSchoolReport}
                >
                  View School Report
                  <LaunchIcon style={{ fontSize: "14px" }} />
                </button>
              )}
              {canExportData &&
                selectedEvent?.scheduled_events &&
                paginatedStudents.length > 0 && (
                  <div className="export-container">
                    {isExporting ? (
                      <div className="export-loading">
                        <img
                          src={downloading}
                          alt="Loading"
                          className="export-loader"
                        />
                        <span>Exporting...</span>
                      </div>
                    ) : (
                      <select
                        className="operation-select export-operations"
                        onChange={(e) => handleExport(e.target.value)}
                        defaultValue=""
                        disabled={isExporting}
                      >
                        <option value="" disabled>
                          Export
                        </option>
                        <option value="csv">Export CSV</option>
                        <option value="pdf">Export PDF</option>
                      </select>
                    )}

                    {exportMessage.type && (
                      <div className={`export-message ${exportMessage.type}`}>
                        {exportMessage.text}
                      </div>
                    )}
                  </div>
                )}
              <select
                className="operation-select bulk-operations"
                value={attemptFilter}
                onChange={(e) => onAttemptFilterChange(e.target.value)}
              >
                <option value="ALL">All</option>
                <option value="ATTEMPTED">Attempted</option>
                <option value="NON_ATTEMPTED">Non-Attempted</option>
              </select>
              <Pagination
                totalItems={
                  attemptFilter === "ATTEMPTED"
                    ? eventAttemptedTotal
                    : attemptFilter === "NON_ATTEMPTED"
                      ? eventStudentsTotal - eventAttemptedTotal
                      : eventStudentsTotal
                }
                currentPage={eventCurrentPage}
                itemsPerPage={eventItemsPerPage}
                itemsPerPageOptions={[100, 200, 500, 1000, 2000]}
                onPageChange={onEventPageChange}
                onItemsPerPageChange={onEventItemsPerPageChange}
              />
            </motion.div>
          </div>
          <AnimatePresence mode="wait">
            <motion.div
              className="event-content-wrapper"
              key={eventContentKey}
              initial={{ y: 24, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 24, opacity: 0 }}
              transition={{
                duration: 0.55,
                ease: [0.25, 0.1, 0.25, 1],
              }}
            >
              {studentsWithDeeplinks.length > 0 ? (
                <StudentTable
                  data={studentsWithDeeplinks}
                  columns={columns}
                  selectedRows={selectedStudents}
                  onSelectAll={onSelectAll}
                  onSelectRow={onSelectRow}
                />
              ) : (
                <div className="error-message">No students found</div>
              )}
            </motion.div>
          </AnimatePresence>
        </>
      ) : (
        <></>
      )}
    </div>
  );
};

export default EventsTabContent;
